import {
  StickyNote,
  Music,
  MessageCircle,
  Users,
  Gamepad2,
  Heart,
  KanbanSquare,
} from "lucide-react";
import type { AppInfo } from "@/stores/open-apps-store";

/** Shelves the Store groups apps under. */
export type AppCategory = "Social" | "Create" | "Play" | "Wellbeing";

export const APP_CATEGORIES: AppCategory[] = ["Social", "Create", "Play", "Wellbeing"];

export interface AppRegistryEntry extends AppInfo {
  description: string;
  available: boolean;
  /** Store shelf. */
  category: AppCategory;
  /** One line for the Store hero and detail page. */
  tagline: string;
  /** What you actually get — shown as bullets on the detail page. */
  highlights: string[];
  /** Carried on the Store's featured carousel. */
  featured?: boolean;
  /**
   * Editorial ranking for the "Popular" shelf. Hand-set, not telemetry —
   * there is no install-count pipeline, and inventing one would be a lie.
   * Lower sorts first.
   */
  rank: number;
}

export const APP_REGISTRY: AppRegistryEntry[] = [
  {
    id: "notes",
    name: "Notes",
    description: "Your personal notebook and diary",
    icon: StickyNote,
    color: "bg-gradient-to-br from-[#ffb64d] via-[#f08a24] to-[#ff477e]",
    route: "/notes",
    available: true,
    category: "Create",
    tagline: "A notebook that keeps up with you.",
    highlights: [
      "Rich pages with images, GIFs and files",
      "Share a page and edit it together, live",
      "A collaborative whiteboard on any note",
    ],
    featured: true,
    rank: 3,
  },
  {
    id: "music",
    name: "Music",
    description: "Listen, discover, and share music",
    icon: Music,
    color: "bg-gradient-to-br from-[#2ddf8f] via-[#16a36b] to-[#0f5f47]",
    route: "/music",
    available: false,
    category: "Create",
    tagline: "Your library, your people, one feed.",
    highlights: [
      "Listen along with friends",
      "Build and share playlists",
      "Discover what your circle has on repeat",
    ],
    rank: 9,
  },
  {
    id: "messenger",
    name: "Chat",
    description: "Private and group messaging",
    icon: MessageCircle,
    color: "bg-gradient-to-br from-[#5a1728] via-[#a52040] to-[#ff477e]",
    route: "/messenger",
    available: true,
    category: "Social",
    tagline: "Private messages, group chats, calls.",
    highlights: [
      "End-to-end encrypted conversations",
      "Voice notes, GIFs, photos and reactions",
      "Voice and video calls built in",
    ],
    featured: true,
    rank: 1,
  },
  {
    id: "communities",
    name: "Communities",
    description: "Servers, channels, and voice chat",
    icon: Users,
    color: "bg-gradient-to-br from-[#7a3cff] via-[#8f1d6b] to-[#ff477e]",
    route: "/communities",
    available: true,
    category: "Social",
    tagline: "Servers, channels and voice, your way.",
    highlights: [
      "Channels with per-role permissions",
      "Voice rooms and rich text posts",
      "Custom invites and a vanity link",
    ],
    rank: 4,
  },
  {
    id: "games",
    name: "Games",
    description: "Play, compete, and share scores",
    icon: Gamepad2,
    color: "bg-gradient-to-br from-[#ff6b35] via-[#c83050] to-[#6b1028]",
    route: "/games",
    available: true,
    category: "Play",
    tagline: "Party games and card tables with friends.",
    highlights: [
      "Multiplayer party and card games, no downloads",
      "Shared XP, levels and achievements",
      "Leaderboards across every game",
    ],
    featured: true,
    rank: 2,
  },
  {
    id: "health",
    name: "Health",
    description: "Nutrition, fitness, cycle, fasting, sleep & more",
    icon: Heart,
    color: "bg-gradient-to-br from-[#ff8fac] via-[#ff477e] to-[#800020]",
    route: "/health",
    available: true,
    category: "Wellbeing",
    tagline: "Food, training, sleep and cycle in one place.",
    highlights: [
      "1.6M-food nutrition database",
      "Fasting, cycle and sleep tracking",
      "One dashboard for the whole day",
    ],
    rank: 5,
  },
  {
    id: "brief",
    name: "Brief",
    description: "Plan projects, track tasks, ship work",
    icon: KanbanSquare,
    color: "bg-gradient-to-br from-[#7db0ff] via-[#4C8DFF] to-[#1e40af]",
    route: "/brief",
    available: true,
    category: "Create",
    tagline: "Plan the work, then ship it.",
    highlights: [
      "Boards, lists and checklists",
      "Attach files and links to any card",
      "Live updates as your team moves cards",
    ],
    rank: 6,
  },
];

/** Look up an app by its route prefix (e.g. "/notes/abc" matches "/notes") */
export function findAppByRoute(
  pathname: string
): AppRegistryEntry | undefined {
  return APP_REGISTRY
    .filter((app) => pathname.startsWith(app.route))
    .sort((a, b) => b.route.length - a.route.length)[0];
}

/** Look up an app by its ID */
export function findAppById(id: string): AppRegistryEntry | undefined {
  return APP_REGISTRY.find((app) => app.id === id);
}

/** Per-app accent color map for Tailwind classes */
export const APP_ACCENT_COLORS: Record<
  string,
  { bg: string; border: string; text: string }
> = {
  notes: {
    bg: "bg-amber-500/10",
    border: "border-l-amber-500",
    text: "text-amber-600 dark:text-amber-400",
  },
  music: {
    bg: "bg-green-500/10",
    border: "border-l-green-500",
    text: "text-green-600 dark:text-green-400",
  },
  messenger: {
    bg: "bg-blue-500/10",
    border: "border-l-blue-500",
    text: "text-blue-600 dark:text-blue-400",
  },
  communities: {
    bg: "bg-purple-500/10",
    border: "border-l-purple-500",
    text: "text-purple-600 dark:text-purple-400",
  },
  games: {
    bg: "bg-red-500/10",
    border: "border-l-red-500",
    text: "text-red-600 dark:text-red-400",
  },
  mimic: {
    bg: "bg-cyan-500/10",
    border: "border-l-cyan-500",
    text: "text-cyan-600 dark:text-cyan-300",
  },
  health: {
    bg: "bg-pink-500/10",
    border: "border-l-pink-500",
    text: "text-pink-600 dark:text-pink-400",
  },
  brief: {
    bg: "bg-blue-500/10",
    border: "border-l-[#4C8DFF]",
    text: "text-[#4C8DFF] dark:text-[#7db0ff]",
  },
};
